import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { ActionEntry, loadActions } from '@/results/results.api';
import { fancyView, outcomesText, whenText } from '@/results/format';

const ACTION_NAMES: Record<string, string> = {
  CLOSE: 'Closed', SETTLE: 'Result', VOID: 'No result', RESETTLE: 'Correction', UNSETTLE: 'Undo result', VOID_BETS: 'Void bets',
  APPROVE: 'Approval', RETRY: 'Retry', CANCEL: 'Reject / withdraw', DEBIT_RECHECK: 'Debit re-check', DEBIT_WRITE_OFF: 'Debit write-off',
  DEFICITS_CLEAR: 'Legacy deficits cleared',
};
const ROLE_NAMES: Record<string, string> = { SUPERVISOR: 'supervisor', OPERATOR: 'operator', NONE: 'not a result user' };

/**
 * GS-925 — who did what on the result site, newest first, from gs-settlement-service's operator action log (owner,
 * 2026-10-16: "capture the user who is providing what result and if approval then who has approved — needs to identify
 * RCA"). Every action is there, whatever came of it: a result, a correction and its approval, a refusal (in the server's
 * own words), a person who is not a result user. Inside a market card ({@code marketId}, compact) it is that market's
 * history; on its own it is the Activity section, filtered by person.
 */
@Component
export default class ActionLog extends Vue {
  /** A market's history (inside its card); empty = every action, the Activity section. */
  @Prop({ default: '' }) readonly marketId!: string;
  /** The market's selections, to name a winner or an outcome. */
  @Prop({ default: (): Array<{ id: number; name: string }> => [] }) readonly selections!: Array<{ id: number; name: string }>;
  /** A fancy line's subtype, to show its runs as the line does. */
  @Prop({ default: null }) readonly subtype!: number | null;
  @Prop({ type: Boolean, default: false }) readonly compact!: boolean;

  private items: ActionEntry[] = [];
  private loading = false;
  private error = '';
  private actor = '';

  get names(): Record<number, string> {
    const out: Record<number, string> = {};
    this.selections.forEach((s) => { out[s.id] = s.name; });
    return out;
  }

  created(): void {
    this.load();
  }

  @Watch('marketId')
  onMarket(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.items = await loadActions({ marketId: this.marketId || undefined, actor: this.actor.trim() || undefined,
        limit: this.compact ? 50 : 200 });
    } catch (e) {
      this.error = (e as Error).message || 'The history could not be read.';
      this.items = [];
    } finally {
      this.loading = false;
    }
  }

  actionName(a: ActionEntry): string {
    return ACTION_NAMES[a.action] || a.action;
  }

  roleText(a: ActionEntry): string {
    return ROLE_NAMES[a.role] || a.role.toLowerCase();
  }

  /** ok = done (or asked for, when it needs a second supervisor); refused; error. */
  tone(a: ActionEntry): string {
    return a.outcome === 'OK' ? 'success' : a.outcome === 'REFUSED' ? 'warning' : 'danger';
  }

  outcomeText(a: ActionEntry): string {
    return a.outcome === 'OK' ? 'Done' : a.outcome === 'REFUSED' ? 'Refused' : 'Failed';
  }

  /** What they entered: the winner, the runs, every selection's outcome, why no result, the bets, and their reason. */
  detail(a: ActionEntry): string[] {
    const r = a.request || {};
    const out: string[] = [];
    if (r.winnerSelectionId !== undefined && r.winnerSelectionId !== null) {
      out.push('Winner: ' + (this.names[r.winnerSelectionId] || 'selection ' + r.winnerSelectionId));
    }
    if (r.resultValue !== undefined && r.resultValue !== null) {
      out.push('Result: ' + fancyView(r.resultValue, this.subtype));
    }
    if (r.results && r.results.length) {
      out.push('Result: ' + outcomesText(r.results, this.names));
    }
    if (r.voidReason) {
      out.push('No result: ' + r.voidReason.toLowerCase().replace(/_/g, ' '));
    }
    if (r.betIds && r.betIds.length) {
      out.push(r.betIds.length + ' bet' + (r.betIds.length === 1 ? '' : 's') + ': ' + r.betIds.join(', '));
    }
    if (r.force) {
      out.push('Forced past the live-market guard');
    }
    if (r.reason) {
      out.push('Reason: ' + r.reason);
    }
    if (a.outcome !== 'OK' && a.message) {
      out.push((a.code ? a.code + ' — ' : '') + a.message);
    }
    return out;
  }

  where(a: ActionEntry): string {
    const parts: string[] = [];
    if (!this.marketId && a.marketId) {
      parts.push('Market ' + a.marketId);
    }
    if (a.runId) {
      parts.push('run ' + a.runId);
    }
    if (a.caseId) {
      parts.push('case ' + a.caseId);
    }
    return parts.join(' · ');
  }

  when(at: string): string {
    return whenText(at);
  }

  /** The exact time, to the second, for the RCA (the list shows the friendly one). */
  exact(at: string): string {
    return (at || '').substring(0, 19);
  }
}
